import type { Admin, Permission } from "../domain.ts";
const mznOrganizationId = "00000000-0000-4000-8000-000000000001";
const servicePermissions = new Set(["events.read", "events.manage"]);
function isMznOrganization(value: unknown): boolean {
  return !!value && typeof value === "object" && "id" in value && value.id === mznOrganizationId;
}
const permissions = new Set<Permission>([
  "organizations.manage", "qualification.review", "products.read", "products.manage", "sellerProducts.manage", "access.manage", "events.read", "events.manage", "events.configure",
  "finance.read", "finance.manage", "finance.access.manage",
  "crm.read",
  "moderation.read",
  "moderation.resolve",
  "users.suspend",
  "audit.read",
  "competition.weights",
]);
export function parseAdmin(value: unknown): Admin {
  if (!value || typeof value !== "object")
    throw new Error("Invalid admin session");
  const v = value as Record<string, unknown>;
  if (
    typeof v.id !== "string" ||
    !v.id ||
    typeof v.name !== "string" ||
    typeof v.csrfToken !== "string" ||
    !v.csrfToken ||
    !["MANAGER", "ADMIN", "OWNER"].includes(v.rank as string) ||
    (v.rank === "OWNER" && (v.scope === "MZN_PLATFORM" || !v.organization)) ||
    (v.rank === "ADMIN" && (v.scope !== "MZN_PLATFORM" ||
      (Array.isArray(v.permissions) && v.permissions.some(p => String(p).startsWith("finance.") ||
        (servicePermissions.has(String(p)) && !isMznOrganization(v.organization)))))) ||
    !["MZN_PLATFORM", "ORGANIZATION_PRODUCTS", "PRODUCT_CATALOG"].includes(v.scope as string) ||
    !Array.isArray(v.permissions) ||
    !v.permissions.length ||
    !v.permissions.includes("products.read") ||
    v.permissions.some((p) => !permissions.has(p as Permission)) ||
    (v.permissions.includes("finance.manage") && !v.permissions.includes("finance.read")) ||
    (v.scope === "PRODUCT_CATALOG" && v.permissions.some((p) => !["sellerProducts.manage", "products.read", "finance.read", "finance.manage", "finance.access.manage"].includes(String(p)))) ||
    (v.scope === "ORGANIZATION_PRODUCTS" && (!v.permissions.some(p => ["events.read", "events.manage", "finance.read", "finance.access.manage"].includes(String(p))) ||
      v.permissions.some((p) => !["sellerProducts.manage", "products.read", "events.read", "events.manage", "finance.read", "finance.manage", "finance.access.manage"].includes(String(p)))))
  ) {
    throw new Error("An authorized admin session is required");
  }
  if (v.organization !== undefined) {
    const org = v.organization as Record<string, unknown> | null;
    if (!org || typeof org.id !== "string" || !org.id || typeof org.name !== "string" || !org.name) throw new Error("Invalid organization context");
  }
  return v as Admin;
}
export function normalizePhone(value: string): string {
  const digits = value
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[\s()-]/g, "");
  const phone = digits.startsWith("00") ? "+" + digits.slice(2) : digits;
  if (!/^\+[1-9]\d{7,14}$/.test(phone)) throw new Error("PHONE_FORMAT");
  return phone;
}
export function can(admin: Admin | null, permission: Permission): boolean {
  return !!admin && (admin.rank === "MANAGER" || (admin.rank === "OWNER" && !!admin.organization && admin.scope !== "MZN_PLATFORM") || (admin.rank === "ADMIN" && admin.scope === "MZN_PLATFORM" &&
    !permission.startsWith("finance.") && (!servicePermissions.has(permission) || isMznOrganization(admin.organization)))) &&
    (permission === "products.read" || permission === "sellerProducts.manage" || ["finance.read", "finance.manage", "finance.access.manage"].includes(permission) || admin.scope === "MZN_PLATFORM" ||
      (admin.scope === "ORGANIZATION_PRODUCTS" && ["events.read", "events.manage"].includes(permission))) && admin.permissions.includes(permission);
}

export function canOpenFinance(admin: Admin): boolean {
  return can(admin, "finance.read") || can(admin, "finance.access.manage");
}
export function landing(admin: Admin): string {
  return can(admin, "moderation.read") ? "/app/dashboard" : "/app/products";
}

/** Service authority alone does not select or entitle a purchased workspace. */
export function canOpenService(admin: Admin, search: string): boolean {
  const query = new URLSearchParams(search);
  return (can(admin, "events.read") || can(admin, "events.manage")) && (!admin.organization || query.get("organizerId") === admin.organization.id) && !!query.get("organizerId")?.trim() && !!query.get("serviceProductId")?.trim();
}
